/** Dialogs on an agent's page: editing one of its folders, and deleting the agent. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Save, Trash2 } from 'lucide-react';
import type { StorageAgentDTO, StorageLocationSummary } from '@prism/contracts';
import { api, errorDetail, type ApiError } from '../../../api/client';
import { ErrorBanner } from '../../../components/ErrorBanner';
import { FolderPicker, agentRootLabel } from '../../../components/FolderPicker';
import { Modal } from '../../../components/Modal';
import { Segmented } from '../../../components/Segmented';
import { toast } from '../../../components/Toasts';

/** Rename a folder, point it at another folder under the storage root, or change how PRISM uses it. */
export function EditFolderDialog({ loc, agentId, onClose }: { loc: StorageLocationSummary; agentId: string; onClose: () => void }) {
  const [name, setName] = useState(loc.name);
  const [path, setPath] = useState(loc.agentFolder!.path);
  const [mode, setMode] = useState<'managed' | 'in_place'>(loc.agentFolder!.mode === 'in_place' ? 'in_place' : 'managed');
  const [confirm, setConfirm] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const moved = path !== loc.agentFolder!.path;
  async function save(confirmed = false) {
    setBusy(true);
    try {
      await api(`/api/v1/storage-locations/${loc.id}`, {
        method: 'PATCH',
        json: { name: name.trim(), ...(moved ? { path } : {}), ...(loc.purpose === 'storage' && mode !== loc.agentFolder!.mode ? { mode } : {}), confirm: confirmed },
      });
      toast(`Saved ${name}`);
      onClose();
    } catch (err) {
      const e = err as ApiError;
      if (e.code === 'CONFIRMATION_REQUIRED') setConfirm(e.message);
      else toast(errorDetail(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={`Edit ${loc.name}`} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      {confirm
        ? <button className="btn danger" disabled={busy} onClick={() => void save(true)} data-testid="folder-confirm-move">Move it anyway</button>
        : <button className="btn primary" disabled={busy || !name.trim()} onClick={() => void save()} data-testid="folder-save"><Save size={14} /> Save</button>}
    </>}>
      {confirm && <div className="banner warning" data-testid="folder-move-warning"><div className="banner-body"><strong>This folder holds files</strong><span>{confirm}</span></div></div>}
      <div className="field"><label htmlFor="f-name">Name</label><input id="f-name" className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
      {loc.purpose === 'storage' && (
        <div className="field"><label>How PRISM uses it</label>
          <Segmented role="radiogroup" value={mode} onChange={setMode} options={[
            { value: 'managed', label: 'PRISM-managed storage' }, { value: 'in_place', label: 'Archive catalogued in place' },
          ]} /></div>
      )}
      <div className="field"><label>Folder</label>
        <FolderPicker path={path} onPath={(p) => { setPath(p); setConfirm(null); }} source={{ type: 'agent', agentId, base: loc.agentFolder!.base }} />
        {moved && <span className="help" style={{ color: 'var(--warning)' }}>PRISM will use {agentRootLabel(loc.agentFolder!.base)}/{path || ''} instead of {agentRootLabel(loc.agentFolder!.base)}/{loc.agentFolder!.path}. Files are not moved on the server; move them yourself first, or files PRISM keeps here show as missing.</span>}
      </div>
    </Modal>
  );
}

/** Deleting an agent warns first, lists what stops it, and needs the name typed. */
export function DeleteAgentDialog({ agent, onClose, onDeleted }: { agent: StorageAgentDTO; onClose: () => void; onDeleted: () => void }) {
  const impact = useQuery({ queryKey: ['agent-deletion', agent.id], queryFn: () => api<{ folders: number; files: number; blocking: string[] }>(`/api/v1/agents/${agent.id}/deletion`) });
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const i = impact.data;
  const blocked = Boolean(i?.blocking.length);
  return (
    <Modal title={`Delete ${agent.name}?`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{blocked ? 'Close' : 'Cancel'}</button>
      {!blocked && <button className="btn danger" disabled={!i || typed !== agent.name || busy} data-testid="agent-delete-confirm" onClick={() => void (async () => {
        setBusy(true);
        try {
          await api(`/api/v1/agents/${agent.id}?confirm=${encodeURIComponent(typed)}`, { method: 'DELETE' });
          toast(`Deleted ${agent.name}`);
          onDeleted();
        } catch (err) {
          toast(errorDetail(err), 'error');
        } finally {
          setBusy(false);
        }
      })()}><Trash2 size={14} /> Delete agent</button>}
    </>}>
      {!i ? <div className="skeleton" style={{ height: 80 }} /> : blocked ? (
        <ErrorBanner title="It cannot be deleted yet">{i.blocking.map((b) => <span key={b}>{b}</span>)}</ErrorBanner>
      ) : (
        <>
          <div className="banner warning"><div className="banner-body">
            <strong>The agent is locked out and leaves PRISM</strong>
            <span>{i.folders ? `Its ${i.folders} folder${i.folders === 1 ? '' : 's'} and its watch folders are removed from PRISM. ` : ''}Files on the agent’s server are not touched. To use the server again, add a new agent.</span>
          </div></div>
          <div className="field"><label htmlFor="del-agent">Type <strong>{agent.name}</strong> to confirm</label>
            <input id="del-agent" className="input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" /></div>
        </>
      )}
    </Modal>
  );
}
